import ProviderAPIClient from '@/features/auth/ProviderAPIClient'
import { isStandardUuidString } from '@/utils/orderingOrgId'
import * as Sentry from '@sentry/react'

export const NPI_IN_USE_MESSAGE = 'This NPI is already assigned to a member of your organization.'

export const IMDB_USER_ID_CLAIM = 'https://inflectionmedicine.com/user_id'

export interface NpiInUseCheckResponse {
  inUse: boolean
  userId: string | null
}

async function readErrorDetail(response: Response): Promise<string> {
  try {
    const j = (await response.json()) as { detail?: string; message?: string }
    if (typeof j.detail === 'string' && j.detail) return j.detail
    if (typeof j.message === 'string' && j.message) return j.message
  } catch {
    /* ignore */
  }
  return response.statusText || `Request failed (${response.status})`
}

/** Client-side gate before calling GET /auth/users/npi-in-use. */
export function isNpiFormatValidForLookup(npi: string): boolean {
  const trimmed = npi.trim()
  return trimmed.length > 0 && /^\d{1,10}$/.test(trimmed)
}

/** IMDB internal user UUID from provider-api JWT when present. */
export function tryImdbUserIdFromBearerJwt(token: string | null | undefined): string | null {
  if (!token?.includes('.')) {
    return null
  }
  try {
    const payload = JSON.parse(atob(token.split('.')[1]!)) as Record<string, unknown>
    const candidates = [payload[IMDB_USER_ID_CLAIM], payload['user_id'], payload['sub']]
    for (const raw of candidates) {
      if (typeof raw === 'string') {
        const trimmed = raw.trim()
        if (isStandardUuidString(trimmed)) {
          return trimmed
        }
      }
    }
    return null
  } catch {
    return null
  }
}

/**
 * GET /auth/users/npi-in-use?npi=…&excludeUserId=…
 * Org scope is derived from the JWT; do not pass org_id from the client.
 */
export async function lookupNpiInUse(
  npi: string,
  excludeUserId?: string | null,
  signal?: AbortSignal
): Promise<NpiInUseCheckResponse> {
  return Sentry.startSpan(
    {
      op: 'http.client',
      name: 'GET /auth/users/npi-in-use',
    },
    async (span) => {
      const trimmed = npi.trim()
      const params = new URLSearchParams({ npi: trimmed })
      if (excludeUserId?.trim() && isStandardUuidString(excludeUserId.trim())) {
        params.set('excludeUserId', excludeUserId.trim())
      }

      const client = ProviderAPIClient.getInstance()
      const path = `/auth/users/npi-in-use?${params.toString()}`
      const response = await client.get(path, undefined, true, signal)

      span.setAttribute('http.status_code', response.status)

      if (response.ok) {
        const data = (await response.json()) as NpiInUseCheckResponse
        span.setAttribute('npi_in_use', data.inUse)
        return data
      }

      const detail = await readErrorDetail(response)
      if (response.status >= 500) {
        Sentry.captureMessage('npi-in-use lookup failed', {
          level: 'error',
          extra: { status: response.status, detail },
        })
      }
      throw new Error(detail)
    }
  )
}

export interface CheckOrgNpiAvailableParams {
  npi: string
  /** When editing an existing user, pass their IMDB user UUID. */
  excludeUserId?: string | null
  /**
   * Edit flow: skip the lookup when the NPI is unchanged from the loaded value
   * (including when the user re-enters their current NPI after editing other fields).
   */
  skipWhenUnchangedFrom?: string | null
  signal?: AbortSignal
}

/**
 * Organizational NPI uniqueness check before creating or updating a user.
 *
 * @returns `{ ok: true }` when the NPI may be used; `{ ok: false, message }` when taken in the org.
 */
export async function checkOrgNpiAvailable(
  params: CheckOrgNpiAvailableParams
): Promise<{ ok: true } | { ok: false; message: string }> {
  const npi = params.npi.trim()
  if (!npi) {
    return { ok: true }
  }

  if (!isNpiFormatValidForLookup(npi)) {
    return { ok: false, message: 'NPI must be numeric and at most 10 digits.' }
  }

  const unchanged = params.skipWhenUnchangedFrom?.trim()
  if (unchanged !== undefined && unchanged !== '' && npi === unchanged) {
    return { ok: true }
  }

  const data = await lookupNpiInUse(npi, params.excludeUserId, params.signal)
  if (!data.inUse) {
    return { ok: true }
  }

  return { ok: false, message: NPI_IN_USE_MESSAGE }
}
